import axios from 'axios';
import { auth } from '@/config/api';

// Create axios instance for API calls to the Auth Service
const apiClient = axios.create({
  baseURL: auth,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Important for cookies
});

// Add request interceptor to handle authentication and CORS
apiClient.interceptors.request.use(
  config => {
    // Add the token to the request if it exists in localStorage
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token storage and IP validation
apiClient.interceptors.response.use(
  response => {
    // If the response contains a token, store it
    if (response.data && response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
    }

    // Check for IP validation failure
    if (response.data && response.data.isAuthenticated === false && response.data.error) {
      // Clear authentication data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      document.cookie = 'session_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      // Redirect to login if IP mismatch
      if (response.data.error.includes('IP') || response.data.error.includes('mismatch')) {
        window.location.href = '/login';
        return Promise.reject(new Error('IP address mismatch - please login again'));
      }
    }

    return response;
  },
  error => {
    // If the error is due to an expired token or unauthorized access, clear it
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      document.cookie = 'session_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      // Redirect to login for authentication errors
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Authenticate with Google
 * @param {string} idToken - The Google ID token
 * @returns {Promise<Object>} Authentication result
 */
export async function authenticateWithGoogle(idToken) {
  if (!idToken) {
    throw new Error('No ID token received from Google');
  }

  const response = await apiClient.post('/google', { token: idToken });
  return response.data;
}

/**
 * Authenticate with Spotify
 * @param {string} code - The authorization code from Spotify
 * @param {string} redirectUri - The redirect URI used in the authorization request
 * @returns {Promise<Object>} Authentication result
 */
export async function authenticateWithSpotify(code, redirectUri) {
  if (!code) {
    throw new Error('No authorization code received from Spotify');
  }

  const response = await apiClient.post('/spotify', { code, redirectUri });
  return response.data;
}

/**
 * Fetch current user data
 * @returns {Promise<Object>} User data
 */
export async function fetchUserData() {
  const response = await apiClient.get('/user');

  // The new auth service returns { isAuthenticated, user } structure
  if (response.data.isAuthenticated && response.data.user) {
    // Add default values for properties not provided by auth service
    return {
      ...response.data.user,
      level: response.data.user.level || 1,
      points: response.data.user.points || 0,
      albums: response.data.user.albums || []
    };
  }

  // If not authenticated (including IP mismatch), clear local storage and throw error
  if (response.data.isAuthenticated === false) {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    document.cookie = 'session_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    throw new Error('Authentication failed - IP address mismatch or invalid token');
  }

  throw new Error('User not authenticated');
}

/**
 * Update user profile
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} Updated user data
 */
export async function updateUserProfile(userData) {
  const response = await apiClient.put('/user', userData);

  // Add default values for properties not provided by auth service
  return {
    ...response.data.user,
    level: response.data.user.level || 1,
    points: response.data.user.points || 0,
    albums: response.data.user.albums || []
  };
}

/**
 * Logout the current user
 * @returns {Promise<void>}
 */
export async function logout() {
  await apiClient.post('/logout');

  // Clear the token from localStorage
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_data');

  // Clear any cookies
  document.cookie = 'session_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
}

/**
 * Delete the current user account
 * @returns {Promise<void>}
 */
export async function deleteUser() {
  // Call the delete user endpoint
  await apiClient.delete('/user');

  // Clear the token from localStorage
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_data');

  // Clear any cookies
  document.cookie = 'session_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
}
